import React, { useState } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import FileUpload from './FileUpload';
import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';

interface TemplateParameter {
  id: string;
  label: string;
  type: 'text' | 'image' | 'textarea';
  placeholder?: string;
  required: boolean;
}

interface TemplateFormProps {
  parameters: TemplateParameter[];
  onSubmit: (values: Record<string, any>) => Promise<void>;
  loading?: boolean;
  isAuthenticated: boolean;
  onLoginClick?: () => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ parameters, onSubmit, loading = false, isAuthenticated, onLoginClick }) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File>>({});  // Track uploaded files

  const handleChange = (paramId: string, value: any) => {
    setValues(prev => ({ ...prev, [paramId]: value }));
    // Clear error when user starts typing
    if (errors[paramId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[paramId];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    parameters.forEach(param => {
      if (param.required) {
        if (param.type === 'image') {
          // For image fields, check if file is uploaded
          if (!files[param.id]) {
            newErrors[param.id] = `${param.label} is required`;
          }
        } else {
          // For text/textarea fields, check values
          if (!values[param.id]) {
            newErrors[param.id] = `${param.label} is required`;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      // Process any image files to base64
      const processedValues = { ...values };

      for (const [paramId, file] of Object.entries(files)) {
        let fileToProcess = file;

        // Convert HEIC/HEIF files to JPEG
        if (file.type === 'image/heic' || file.type === 'image/heif' ||
          file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
          try {
            console.log('Converting HEIC/HEIF file to JPEG...');

            const convertedBlob = await heic2any({
              blob: file,
              toType: 'image/jpeg',
              quality: 0.9
            });

            const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            fileToProcess = new File([blob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), {
              type: 'image/jpeg'
            });

            console.log('HEIC/HEIF conversion successful');
          } catch (conversionError) {
            console.error('Failed to convert HEIC/HEIF file:', conversionError);
            throw new Error('Failed to convert HEIC/HEIF image. Please try converting it to JPEG first.');
          }
        }

        // Compress image
        const fileSizeMB = fileToProcess.size / 1024 / 1024;
        console.log(`Original file size: ${fileSizeMB.toFixed(2)} MB`);

        let fileToRead = fileToProcess;
        if (fileSizeMB > 3) {
          try {
            console.log('Compressing image to reduce size...');

            const options = {
              maxSizeMB: 5,
              maxWidthOrHeight: 2048,
              useWebWorker: true,
              fileType: 'image/jpeg',
              initialQuality: 0.85
            };

            const compressedFile = await imageCompression(fileToProcess, options);
            const compressedSizeMB = compressedFile.size / 1024 / 1024;
            console.log(`Compressed file size: ${compressedSizeMB.toFixed(2)} MB`);

            fileToRead = compressedFile;
          } catch (compressionError) {
            console.error('Image compression failed, using original:', compressionError);
            fileToRead = fileToProcess;
          }
        }

        // Convert to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = () => reject(new Error('Failed to read file.'));
          reader.readAsDataURL(fileToRead);
        });

        processedValues[paramId] = base64;
      }

      await onSubmit(processedValues);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error;
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {!isAuthenticated && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <span>Please log in to customize this template</span>
            <Button
              variant="primary"
              size="sm"
              onClick={onLoginClick}
            >
              Login to Customize
            </Button>
          </div>
        </Alert>
      )}

      {parameters.map(param => (
        <Form.Group key={param.id} className="mb-3">
          <Form.Label>
            {param.label}
            {param.required && <span className="text-danger"> *</span>}
          </Form.Label>

          {param.type === 'text' && (
            <Form.Control
              type="text"
              value={values[param.id] || ''}
              onChange={(e) => handleChange(param.id, e.target.value)}
              isInvalid={!!errors[param.id]}
              disabled={!isAuthenticated}
            />
          )}

          {param.type === 'textarea' && (
            <Form.Control
              as="textarea"
              rows={3}
              value={values[param.id] || ''}
              onChange={(e) => handleChange(param.id, e.target.value)}
              isInvalid={!!errors[param.id]}
              disabled={!isAuthenticated}
            />
          )}

          {param.type === 'image' && (
            <FileUpload
              onFileSelected={(file) => {
                if (!file) {
                  // Clear the file
                  setFiles(prev => {
                    const updated = { ...prev };
                    delete updated[param.id];
                    return updated;
                  });
                  return;
                }
                // Store the file for processing on submit
                setFiles(prev => ({ ...prev, [param.id]: file }));
                // Clear error for this field
                if (errors[param.id]) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[param.id];
                    return newErrors;
                  });
                }
              }}
              accept="image/*"
              placeholder={files[param.id] ? files[param.id].name : param.placeholder || 'Upload an image or use camera'}
              showPreview
              disabled={!isAuthenticated}
            />
          )}

          <Form.Control.Feedback type="invalid">
            {errors[param.id]}
          </Form.Control.Feedback>

          {param.placeholder && param.type !== 'image' && (
            <Form.Text className="text-muted">
              {param.placeholder}
            </Form.Text>
          )}
        </Form.Group>
      ))}

      {Object.keys(errors).length > 0 && (
        <Alert variant="danger">
          Please fill in all required fields
        </Alert>
      )}

      <div className="d-grid">
        <Button
          variant="primary"
          size="lg"
          type="submit"
          disabled={loading || !isAuthenticated}
        >
          {!isAuthenticated ? 'Login Required' :
            loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Generating...
              </>
            ) : (
              'Generate My Product'
            )}
        </Button>
      </div>
    </Form>
  );
};

export default TemplateForm;
